import React, { useCallback, useEffect, useState } from 'react';
import { settingsAPI } from '../../config/settings-api';
import { isIndiaClient } from '../../utils/client';
import { helpers } from '../../utils/helpers';
import { ADMIN_PERMISSIONS } from '../../constants/permissions';
import CustomFieldsSettings from './CustomFieldsSettings';
import OrderStatusSettings from './OrderStatusSettings';
import NegativeOrderStatusSettings from './NegativeOrderStatusSettings';
import OrderListColumnsSettings from './OrderListColumnsSettings';
import TimeSlotsSettings from './TimeSlotsSettings';
import DeliverySettings from './DeliverySettings';
import './Settings.css';
import './OrderSettings.css';

const Settings = ({ adminData }) => {
  const isIndia = isIndiaClient(adminData);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const canView = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_SETTINGS)
    || helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_ORDERS);

  const canManageTimeSlots = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.MANAGE_SETTINGS)
    || helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_ORDERS)
    || helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_DISPATCH);

  const canManageCustomFields = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.MANAGE_ORDER_CUSTOM_FIELDS)
    || canManageTimeSlots;

  const canManageOrderStatuses = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.MANAGE_ORDER_STATUSES)
    || canManageTimeSlots;

  const loadConfig = useCallback(async (isSilent = false) => {
    if (!isSilent) {
      setLoading(true);
    }
    try {
      const response = await settingsAPI.getGeneralConfig();
      if (response.success) {
        setConfig(response.data || {});
      }
    } finally {
      if (!isSilent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (canView) loadConfig();
  }, [canView, loadConfig]);

  if (!canView) {
    return (
      <div className="settings-page">
        <div className="settings-loading-state">
          <p>You do not have permission to view settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-page-header">
        <div className="settings-page-title">
          <h1>Settings</h1>
          <p>Configure order fields, delivery charges, and status flow</p>
        </div>
      </div>

      {loading ? (
        <div className="settings-loading-state">
          <div className="settings-loading-spinner" />
          <p>Loading settings...</p>
        </div>
      ) : (
        <div className="settings-grid">
          <DeliverySettings
            minOrderAmount={config?.minOrderAmount}
            deliveryChargeSlabs={config?.deliveryChargeSlabs}
            canManage={canManageTimeSlots}
            onSaved={(data) => {
              setConfig((prev) => (prev
                ? {
                  ...prev,
                  minOrderAmount: data?.minOrderAmount,
                  deliveryChargeSlabs: data?.deliveryChargeSlabs,
                }
                : prev));
            }}
          />
          <TimeSlotsSettings
            defaultTimeSlots={config?.defaultTimeSlots || []}
            timeSlotsList={config?.timeSlotsList || []}
            canManage={canManageTimeSlots}
            onSaved={() => loadConfig(true)}
          />
          <CustomFieldsSettings
            fields={config?.orderCustomFields || []}
            canManage={canManageCustomFields}
            onSaved={(fields) => {
              setConfig((prev) => (prev ? { ...prev, orderCustomFields: fields } : prev));
            }}
          />
          <OrderStatusSettings
            statuses={config?.orderStatuses || []}
            canManage={canManageOrderStatuses}
            onSaved={() => loadConfig(true)}
          />
          <NegativeOrderStatusSettings
            statuses={config?.negativeOrderStatuses || []}
            orderStatuses={config?.orderStatuses || []}
            canManage={canManageOrderStatuses}
            onSaved={(savedStatuses) => {
              setConfig((prev) => (prev ? { ...prev, negativeOrderStatuses: savedStatuses } : prev));
            }}
          />
          <OrderListColumnsSettings
            columns={config?.orderListColumns || []}
            customFields={config?.orderCustomFields || []}
            canManage={canManageOrderStatuses}
            isIndia={isIndia}
            onSaved={(cols) => {
              setConfig((prev) => (prev ? { ...prev, orderListColumns: cols } : prev));
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Settings;
